import { useState, useRef } from "react";
import { Play, Pause, Mic } from "lucide-react";

/* ─── Driver stories ─── */
const DRIVERS = [
  {
    id: "v1",
    audioSrc: "/testimonial-marcus.mp3",
    photo: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/5ee95db14c300643f0ea570a21124caba19b01128aab18557951e3f0f383a74f.jpeg",
    name: "Marcus D.",
    role: "OTR Company Driver",
    market: "Chicago, IL",
    years: "7 yrs",
    story: `Seven years behind the wheel. Four of those at a carrier where I'd call dispatch and get voicemail every single time. My pay kept changing on the settlement sheet — I didn't even know what I was netting until the check hit.

First week at Hylas — brand new Peterbilt, lanes already set, recruiter actually picked up the phone. My settlement was the exact number they told me. 85 cents. Clean miles. No games. I'm home every three weeks like we agreed.

That's what peace of mind feels like on this job.`,
  },
  {
    id: "v2",
    audioSrc: "/testimonial-antonio.mp3",
    photo: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/cde3b8be9eb221d10b82fc848f24017728cb23f4147da42999fb655ad258ba50.jpeg",
    name: "Antonio R.",
    role: "Lease Purchase Driver",
    market: "Indianapolis, IN",
    years: "4 yrs",
    story: `I tried two lease programs before Hylas. Both of them — once you read the fine print — they're taking it all back in fees. I was netting maybe 40% on what they called a "70% deal." Nobody tells you that upfront.

Before I signed anything here, I asked: show me a real settlement. They showed me three. The math was right. 88% of gross — real gross.

Fourteen months in. The truck feels like mine. And the walk-away clause means if tomorrow I decide this ain't for me, I walk. No penalty. No debt chasing me.`,
  },
  {
    id: "v3",
    audioSrc: "/testimonial-sarah.mp3",
    photo: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/c8c3dc481ca54c06720bf4fdf11ac5af9990e852477878c2e1573742941e79a3.jpeg",
    name: "Sarah K.",
    role: "Flatbed Company Driver",
    market: "Nashville, TN",
    years: "5 yrs",
    story: `I met a driver at a fuel stop in Memphis — she had a newer truck than anything I'd seen in five years of driving. I asked her straight up: what do you actually net? She told me. No hesitation. That number was real. That's when I called.

You cannot strap a steel coil to a beat-up trailer and just hope it holds. That's not how flatbed works. My equipment here is maintained.

When something goes wrong — and something always does eventually — somebody answers. After five years of voicemail, that means everything.`,
  },
];

/* ─── Waveform bars ─── */
function Waveform({ playing }) {
  const BAR_COUNT = 28;
  return (
    <div className="flex items-center gap-[2px] h-8" aria-hidden="true">
      {Array.from({ length: BAR_COUNT }).map((_, i) => {
        const baseH = 4 + Math.sin(i * 0.9) * 10 + Math.cos(i * 1.7) * 6;
        return (
          <div
            key={i}
            className={`w-[3px] rounded-full transition-colors duration-300 ${
              playing ? "bg-primary" : "bg-white/15"
            }`}
            style={{
              height: `${Math.max(4, baseH)}px`,
              animation: playing
                ? `wavePulse ${0.6 + (i % 5) * 0.12}s ease-in-out ${(i % 7) * 0.07}s infinite alternate`
                : "none",
            }}
          />
        );
      })}
    </div>
  );
}

/* ─── Audio player card ─── */
function StoryCard({ driver }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio * 100);
  };

  return (
    <div
      data-testid={`story-card-${driver.id}`}
      className={`group flex flex-col bg-[#111] border transition-colors duration-300 ${
        playing ? "border-primary/40" : "border-white/5 hover:border-white/15"
      }`}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={driver.audioSrc}
        preload="none"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onPause={() => setPlaying(false)}
      />

      {/* Driver header */}
      <div className="flex items-center gap-4 px-6 pt-6 pb-4 border-b border-white/5">
        <div className="w-12 h-12 rounded-none overflow-hidden shrink-0 ring-2 ring-white/10">
          <img
            src={driver.photo}
            alt={driver.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0">
          <p className="font-head text-sm font-bold uppercase tracking-[0.1em] text-white leading-tight">
            {driver.name}
          </p>
          <p className="font-head text-[10px] uppercase tracking-[0.2em] text-primary mt-0.5">
            {driver.role}
          </p>
          <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/30 mt-0.5">
            {driver.market} · {driver.years}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <Mic className="w-3 h-3 text-white/20" />
          <span className="font-head text-[9px] uppercase tracking-[0.2em] text-white/20">
            Voice Story
          </span>
        </div>
      </div>

      {/* Story text */}
      <div className="px-6 py-5 flex-1">
        <p className="text-sm text-white/55 leading-[1.8] line-clamp-5">
          {driver.story}
        </p>
      </div>

      {/* Audio player strip */}
      <div className="px-6 pb-6">
        <div
          className={`flex items-center gap-4 px-4 py-3 border transition-colors duration-300 ${
            playing
              ? "bg-primary/5 border-primary/30"
              : "bg-white/[0.03] border-white/8 group-hover:border-white/15"
          }`}
        >
          {/* Play / Pause */}
          <button
            onClick={togglePlay}
            data-testid={`story-play-${driver.id}`}
            aria-label={playing ? "Pause" : "Play story"}
            className={`w-9 h-9 shrink-0 flex items-center justify-center border-2 transition-all duration-200 ${
              playing
                ? "bg-primary border-primary hover:bg-primary/80"
                : "bg-transparent border-white/30 hover:border-primary hover:text-primary"
            }`}
          >
            {playing
              ? <Pause className="w-3.5 h-3.5 text-black fill-black" />
              : <Play  className="w-3.5 h-3.5 text-white ml-0.5" />
            }
          </button>

          {/* Waveform / progress track */}
          <div className="flex-1 flex flex-col gap-1.5 overflow-hidden">
            <Waveform playing={playing} />
            {/* Seek bar */}
            <div
              className="relative h-[3px] bg-white/10 cursor-pointer group/bar"
              onClick={handleSeek}
            >
              <div
                className="absolute inset-y-0 left-0 bg-primary transition-all duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* State label */}
          <span
            className={`font-head text-[9px] uppercase tracking-[0.2em] shrink-0 w-16 text-right transition-colors duration-300 ${
              playing ? "text-primary" : "text-white/25"
            }`}
          >
            {playing ? "Playing..." : "Listen"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Section ─── */
export default function VideoTestimonials() {
  return (
    <>
      {/* Wave pulse keyframe — injected once */}
      <style>{`
        @keyframes wavePulse {
          from { transform: scaleY(0.4); opacity: 0.6; }
          to   { transform: scaleY(1.6); opacity: 1; }
        }
      `}</style>

      <section
        id="driver-stories"
        data-testid="video-testimonials"
        className="bg-[#0A0A0A] py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-head text-xs uppercase tracking-[0.4em] text-primary">
                Driver Stories
              </span>
              <h2 className="font-head text-4xl sm:text-5xl font-bold uppercase tracking-[0.1em] text-white mt-4 leading-none">
                Hear It From<br />
                <span className="text-primary">The Cab</span>
              </h2>
              <p className="mt-5 text-white/50 max-w-lg text-sm leading-relaxed">
                Real drivers. Real pay. No scripts — press play and hear what it's actually like.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="w-8 h-px bg-white/10" />
              <span className="font-head text-[10px] uppercase tracking-[0.3em] text-white/30">
                {DRIVERS.length} stories
              </span>
            </div>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {DRIVERS.map((d) => (
              <StoryCard key={d.id} driver={d} />
            ))}
          </div>

          <p className="mt-8 text-center font-head text-[10px] uppercase tracking-[0.3em] text-white/20">
            Share your own story — contact us at contact@hylascompanion.com
          </p>
        </div>
      </section>
    </>
  );
}
